import React, { useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, Divider, Stack, TextField, Button,
  InputAdornment, IconButton, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, Snackbar, Alert, CircularProgress, Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { getBearerToken, postBearerToken, putBearerToken } from '../../utils/Apis';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import AppShell from '../../layouts/AppShell';

// Create / edit an Admin (client company owner); type is always "Admin".
// Zoom is linked by the admin themselves with "Connect Zoom" in their panel,
// so no Zoom keys are entered here.
const Sa_add_admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname === '/edit_admin';

  const [firstName, setfirstName] = useState('');
  const [phone, set_phone] = useState('');
  const [from_date, set_from_date] = useState('');
  const [to_date, set_to_date] = useState('');
  const [slug_name, set_slug_name] = useState('');
  const [password, set_password] = useState('');
  const [confirm_password, set_confirm_password] = useState('');
  const [is_active, setis_active] = useState('Active');
  const [showPassword, setShowPassword] = useState(false);

  const [loader, setloader] = useState(false);
  const [fetchLoader, setfetchLoader] = useState(false);
  const [toast, setToast] = useState(null);
  const notify = (msg, severity = 'info') => setToast({ msg, severity });

  React.useEffect(() => {
    if (location?.state?.id) fetch_users();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetch_users = async () => {
    setfetchLoader(true);
    getBearerToken('users?_id=' + location.state.id).then((result) => {
      if (result.error === 0) {
        const d = result.data;
        setfirstName(d.first_name || '');
        set_phone(d.phone || '');
        set_from_date(d.from_date ? moment(d.from_date).toDate() : '');
        set_to_date(d.to_date ? moment(d.to_date).toDate() : '');
        set_slug_name(d.slug_name || '');
        setis_active(d.is_active === true ? 'Active' : 'Inactive');
      }
      setfetchLoader(false);
    });
  };

  const submit = async () => {
    setloader(true);
    const url = isEdit ? 'users?_id=' + location.state.id : 'users';

    let json;
    if (!isEdit) {
      json = {
        first_name: firstName, phone, from_date, to_date, slug_name,
        password, show_password: password,
        is_active: is_active === 'Active', type: 'Admin',
      };
    } else if (password !== '') {
      json = {
        first_name: firstName, phone, from_date, to_date,
        password, show_password: password,
        is_active: is_active === 'Active', type: 'Admin',
      };
    } else {
      json = {
        first_name: firstName, phone, from_date, to_date,
        is_active: is_active === 'Active', type: 'Admin',
      };
    }

    if (!isEdit) {
      if (password === '' || slug_name === '' || phone === '') {
        notify('Password, Username and Phone are required.', 'error'); setloader(false); return;
      }
      if (confirm_password !== password) { notify('Passwords do not match.', 'error'); setloader(false); return; }
      postBearerToken(url, json).then((result) => {
        if (result.error === 0) {
          setfirstName(''); set_phone(''); set_from_date(''); set_to_date('');
          set_slug_name(''); set_password(''); set_confirm_password('');
          notify('Admin created successfully.', 'success');
        } else { notify(result.message || 'Something went wrong.', 'error'); }
        setloader(false);
      });
    } else {
      if (phone === '') { notify('Phone is required.', 'error'); setloader(false); return; }
      if (confirm_password !== password) { notify('Passwords do not match.', 'error'); setloader(false); return; }
      putBearerToken(url, json).then((result) => {
        if (result.error === 0) notify('Saved successfully.', 'success');
        else notify(result.message || 'Something went wrong.', 'error');
        setloader(false);
      });
    }
  };

  const DateField = React.forwardRef(({ value, onClick, label }, ref) => (
    <TextField label={label} value={value || ''} onClick={onClick} inputRef={ref} readOnly placeholder="Select date" />
  ));

  return (
    <AppShell title={isEdit ? 'Edit Admin' : 'Add Admin'} breadcrumb={['Admins', isEdit ? 'Edit' : 'Add']}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} color="inherit" onClick={() => navigate('/admin_list')}>
          Back to admins
        </Button>
      </Stack>

      <Box sx={{ maxWidth: 760 }}>
        <Card>
          <CardHeader
            title={isEdit ? 'Edit admin' : 'Add an admin'}
            subheader="An admin is a client account. They link their own Zoom with Connect Zoom after their first login."
            titleTypographyProps={{ variant: 'h5' }}
          />
          <Divider />
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {fetchLoader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={3}>
                <TextField label="Full Name" value={firstName} onChange={(e) => setfirstName(e.target.value)} />
                <TextField label="Mobile Number" value={phone} onChange={(e) => set_phone(e.target.value)} />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <DatePicker selected={from_date} onChange={(d) => set_from_date(d)} customInput={<DateField label="Start Date" />} />
                  <DatePicker selected={to_date} onChange={(d) => set_to_date(d)} customInput={<DateField label="Expiry Date" />} />
                </Stack>

                {!isEdit && (
                  <TextField label="Username" value={slug_name} onChange={(e) => set_slug_name(e.target.value)} helperText="Used to log in" />
                )}

                <TextField
                  label="Password" type={showPassword ? 'text' : 'password'}
                  value={password} onChange={(e) => set_password(e.target.value)}
                  helperText={isEdit ? 'Leave blank to keep the current password' : ' '}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirm Password" type={showPassword ? 'text' : 'password'}
                  value={confirm_password} onChange={(e) => set_confirm_password(e.target.value)}
                />

                <FormControl>
                  <FormLabel sx={{ mb: 0.5, fontWeight: 600 }}>Status</FormLabel>
                  <RadioGroup row value={is_active} onChange={(e) => setis_active(e.target.value)}>
                    <FormControlLabel value="Active" control={<Radio />} label="Active" />
                    <FormControlLabel value="Inactive" control={<Radio />} label="Inactive" />
                  </RadioGroup>
                </FormControl>

                <Divider />
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button color="inherit" onClick={() => navigate('/admin_list')}>Cancel</Button>
                  <Button variant="contained" onClick={submit} disabled={loader}
                    startIcon={loader ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}>
                    {loader ? 'Saving…' : (isEdit ? 'Save changes' : 'Create')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </AppShell>
  );
};

export default Sa_add_admin;
